import { Screen, SectionHeader } from '@/components/layout'
import { RecommendationCard } from '@/components/cards'
import { Button } from '@/components/primitives'
import { ScoreDial } from '@/components/charts/ScoreDial'
import { GeneratedText } from '@/components/GeneratedText'
import { List, PillarRow } from '@/components/rows'
import {
  EmptyState,
  ErrorState,
  InsufficientData,
  LoadingRegion,
  SkeletonCard,
  SkeletonList,
} from '@/components/states'
import { api } from '@/lib/api'
import { useProseState } from '@/lib/analysis'
import { t, type CopyKey } from '@/lib/copy'
import { useNavigate } from '@/lib/router'
import type { ScoreBand } from '@/lib/tokens'
import { combine, useAsync } from '@/lib/useAsync'

/**
 * The score broken into its five pillars, each with what moves it.
 *
 * One of only two screens where `analysisFailed` shows anything at all: the
 * explanation is the point of this screen, so a failed generation earns one
 * quiet retry — and only when the Server says a retry could work. Everywhere
 * else the state is silent, because a user who never knew prose was coming
 * should not be told it did not arrive.
 */
export function HealthScoreScreen() {
  const navigate = useNavigate()
  const score = useAsync(() => api.analysis.score(), [])
  const recommendations = useAsync(() => api.recommendations.list({ status: 'open' }), [])

  const data = score.state.data
  const loaded = combine(score.state, recommendations.state)
  const prose = useProseState(data?.narrativeState, score.reload)

  const dataState =
    score.state.status === 'error'
      ? 'error'
      : score.state.status === 'loading' && !data
        ? 'loading'
        : !data
          ? 'empty'
          : (data.monthsOfData ?? 0) < 2
            ? 'insufficientData'
            : 'ready'

  // The score, the pillars and the levers are all present in every one of
  // these states — only the prose differs, so this refines `ready` and nothing
  // above it. `analysisPending` and `analysisFailed` render the same tree.
  const screenState = dataState === 'ready' ? (prose.state ?? 'ready') : dataState

  // The three states that render the identical tree below. Naming them keeps
  // the promise visible: `analysisPending` shows no spinner and disables
  // nothing, `analysisFailed` is not the error treatment, and the only thing
  // either changes is which slots hold prose rather than their copyKey.
  const rendersContent =
    screenState === 'ready' || screenState === 'analysisPending' || screenState === 'analysisFailed'

  const levers = (recommendations.state.data ?? []).filter((rec) => data?.topLevers?.includes(rec.id))

  const deltaLabel = (() => {
    if (!data || data.delta === null || data.delta === undefined) return null
    const period = t(`dashboard.score.period.${data.deltaPeriod ?? 'lastMonth'}` as CopyKey)
    if (data.delta > 0) return t('dashboard.score.delta.up', { points: data.delta, period })
    if (data.delta < 0) return t('dashboard.score.delta.down', { points: -data.delta, period })
    return t('dashboard.score.delta.flat', { period })
  })()

  return (
    <Screen id="healthScore" state={screenState}>
      {screenState === 'loading' ? (
        <LoadingRegion>
          <SkeletonCard height="var(--space-xxl)" />
          <SkeletonList rows={5} />
        </LoadingRegion>
      ) : screenState === 'error' ? (
        <ErrorState
          titleKey="healthScore.error.title"
          error={loaded.status === 'error' ? loaded.error : undefined}
          onRetry={score.reload}
        />
      ) : screenState === 'empty' ? (
        <EmptyState titleKey="healthScore.empty.title" />
      ) : screenState === 'insufficientData' ? (
        <InsufficientData
          titleKey="healthScore.insufficientData.title"
          bodyKey="healthScore.insufficientData.body"
        />
      ) : data && rendersContent ? (
        <div className="stack">
          <div className="card card-pad-lg row" style={{ gap: 'var(--space-xl)', flexWrap: 'wrap' }}>
            <ScoreDial score={data.score} band={data.band as ScoreBand} />
            <div className="stack-sm" style={{ flex: 1, minWidth: 0 }}>
              <span className="metric-label">{t('healthScore.outOf')}</span>
              {deltaLabel ? <span className="body-secondary">{deltaLabel}</span> : null}
            </div>
          </div>

          {/* Omitted entirely when delta is null — health-score.schema.json. */}
          {data.delta !== null && data.delta !== undefined ? (
            <section>
              <SectionHeader title={t('healthScore.section.whatMoved')} />
              <GeneratedText
                text={data.narrative?.whatMoved}
                copyKey="healthScore.whatMoved.fallback"
                params={{
                  points: Math.abs(data.delta),
                  period: t(`dashboard.score.period.${data.deltaPeriod ?? 'lastMonth'}` as CopyKey),
                }}
                className="body-secondary"
              />
            </section>
          ) : null}

          <section>
            <SectionHeader title={t('healthScore.section.pillars')} />
            <List>
              {data.pillars.map((pillar) => (
                <PillarRow
                  key={pillar.id}
                  pillar={pillar}
                  onSelect={() => navigate(pillar.detailScreen ?? 'cashFlow')}
                />
              ))}
            </List>
          </section>

          {/* Both the keyed fallback and the generated line introduce the
              levers below them, so the section only exists when there are
              some. */}
          {levers.length > 0 ? (
            <section>
              <SectionHeader title={t('healthScore.section.movers')} />
              <GeneratedText
                text={data.narrative?.whatToDo}
                copyKey="healthScore.movers.fallback"
                className="body-secondary"
              />
              <div className="stack-sm">
                {levers.map((rec) => (
                  <RecommendationCard
                    key={rec.id}
                    recommendation={rec}
                    onSelect={() => navigate('recommendationDetail', { id: rec.id })}
                  />
                ))}
              </div>
            </section>
          ) : null}

          {/* The whole of what analysisFailed adds here. Quiet, inline, and
              only when the Server says another attempt could succeed — a
              deploy with no key configured never shows it. No explanation of
              what failed: the screen above is complete either way. */}
          {screenState === 'analysisFailed' && prose.retryable ? (
            <div className="row">
              <Button variant="quiet" onClick={() => prose.retry({ target: 'score' })}>
                {t('healthScore.cta.retryExplain')}
              </Button>
            </div>
          ) : null}
        </div>
      ) : null}
    </Screen>
  )
}
